/**
 * Applying patches to a compiled document — the step between compile and
 * assemble.
 *
 * Patches apply SEQUENTIALLY, in order, to one live copy of the document:
 *
 *  - Every base anchor is resolved against a pristine copy of the compiled
 *    input and mapped to its live node by identity, so an address always
 *    means the node as compiled — removing, inserting or moving earlier in
 *    the sequence never shifts what a later patch points at.
 *  - Fingerprints are checked against that pristine node. A mismatch means
 *    the compiled text changed since the patch was made: conflict, not
 *    applied.
 *  - `{ fromPatch }` anchors reach the output of an earlier insert (its
 *    paragraph / clause, or a node inside the clause) — they are not
 *    fingerprinted: an insert's output is the insert's own payload.
 *
 * Removed nodes stay in the live tree as ghosts (`edit.change: 'removed'`)
 * until the end, so positions next to them stay addressable. A clean
 * result drops ghosts and every `edit` mark; `track` keeps both for the
 * review panel. A tracked document is for display only — never issue or
 * persist it.
 *
 * Locks (ADR-0102 decision 7, see `lock.ts`) are read from the pristine
 * base: a patch that edits, removes or moves locked text, or inserts into a
 * locked node, is a `locked` conflict. Signatures are always locked.
 *
 * Field references (ADR-0102 addendum, see `references.ts`): value nodes
 * with a `sourceKey` in patch content are re-resolved against the CURRENT
 * compile / values, so adapted text follows the form. Fingerprints
 * normalise them to their key (`f2:`), so a changed value is no conflict;
 * stored `f1:` fingerprints are checked the old way (`fingerprintMatches`).
 *
 * Parts (ADR-0102 addendum 3, see `address.ts`): detail-list rows
 * (`replaceRow` / `removeRow` / `insertRow`) and labels (`replaceText` on an
 * option's text, a column header, a caption) resolve like nodes — pristine
 * part → live part by identity — so a row removed or inserted earlier never
 * shifts what a later entry points at. A removed row stays as a ghost row
 * (`edit.change: 'removed'` on the item) until the end, like a node.
 */
import { inlineNodeSchema } from '../ir/schema.js';
import { childLists, collectLockedNodes, formatNodeAddress, instanceRoots, isTextTarget, listAddressableNodes, listAddressableParts, resolveAnchor, resolvePartAnchor, walkPath, } from './address.js';
import { fingerprint, fingerprintMatches, fingerprintRow, instanceFingerprintMatches, partFingerprintMatches } from './fingerprint.js';
import { createReferenceResolver } from './references.js';
import { fingerprintNodeStructure, fingerprintPartStructure } from './structure.js';
import { instanceBlockVersions, isNewerBlockVersion } from './version.js';
import { insertedSectionRef } from './types.js';
/** Inline node types a user may author. No images, labels or document refs. */
const AUTHORABLE_INLINES = new Set(['text', 'strong', 'emphasis', 'lineBreak', 'value', 'sectionRef', 'attachmentRef']);
const MAX_PATCH_TEXT = 50_000;
const MAX_TITLE = 500;
export function isAuthorableInlineContent(content) {
    if (!Array.isArray(content))
        return false;
    let size = 0;
    const ok = (node) => {
        if (!AUTHORABLE_INLINES.has(node.type))
            return false;
        if (node.type === 'text')
            size += node.text.length;
        if (node.type === 'value')
            size += node.text?.length ?? 0;
        if (node.type === 'strong' || node.type === 'emphasis')
            return node.content.every(ok);
        return true;
    };
    for (const item of content) {
        if (!inlineNodeSchema.safeParse(item).success)
            return false;
        if (!ok(item))
            return false;
    }
    return size <= MAX_PATCH_TEXT;
}
/** Plain text of an inline run (values print their text, blanks nothing). */
export function inlinePlainText(content) {
    return content
        .map((n) => {
        switch (n.type) {
            case 'text':
                return n.text;
            case 'strong':
            case 'emphasis':
            case 'label':
                return inlinePlainText(n.content);
            case 'lineBreak':
                return '\n';
            case 'value':
                return n.text ?? '';
            case 'image':
                return '';
            default:
                return n.text ?? n.target;
        }
    })
        .join('');
}
const collectValues = (content, out = []) => {
    for (const n of content) {
        if (n.type === 'value')
            out.push(n);
        else if (n.type === 'strong' || n.type === 'emphasis' || n.type === 'label')
            collectValues(n.content, out);
    }
    return out;
};
const collectBlockValues = (list, out = []) => {
    for (const n of list) {
        for (const run of ownRuns(n))
            collectValues(run, out);
        for (const { list: child } of childLists(n))
            collectBlockValues(child, out);
    }
    return out;
};
const ownRuns = (n) => {
    switch (n.type) {
        case 'paragraph':
        case 'heading':
            return [n.content];
        case 'optionList':
            return n.items.map((i) => i.content);
        case 'table':
            return [...n.columns.flatMap((c) => (c.header ? [c.header] : [])), ...(n.caption ? [n.caption] : [])];
        case 'detailList':
            return n.items.map((i) => i.term);
        default:
            return [];
    }
};
const rowValues = (item) => collectBlockValues(item.description, collectValues(item.term));
const isGhostRow = (item) => item.edit?.change === 'removed';
/** The description lists of removed rows — what lives inside them is gone too. */
const inGhostRow = (node, child) => node.type === 'detailList' && node.items.some((i) => i.description === child && isGhostRow(i));
const valueKey = (v) => (v.sourceKey != null ? `k:${v.sourceKey}` : `t:${v.text ?? ''}`);
const sectionRefsIn = (nodes, out = []) => {
    for (const n of nodes) {
        if (n.edit?.change === 'removed')
            continue;
        if (n.type === 'section')
            out.push(n.ref);
        for (const { list } of childLists(n))
            if (!inGhostRow(n, list))
                sectionRefsIn(list, out);
    }
    return out;
};
const contains = (nodes, needle) => nodes.some((n) => n === needle || childLists(n).some(({ list }) => contains(list, needle)));
const isGhost = (n) => n.edit?.change === 'removed';
const regionLists = (document) => document.parts.flatMap((p) => [p.letterhead, p.body, p.footer].filter((l) => Array.isArray(l)));
function locate(document, needle) {
    const visit = (list, ancestors, ghostRow) => {
        for (let index = 0; index < list.length; index++) {
            const node = list[index];
            if (node === needle)
                return { list, index, ancestors, ghostRow };
            for (const { list: child } of childLists(node)) {
                const hit = visit(child, [...ancestors, node], ghostRow || inGhostRow(node, child));
                if (hit)
                    return hit;
            }
        }
        return null;
    };
    for (const list of regionLists(document)) {
        const hit = visit(list, [], false);
        if (hit)
            return hit;
    }
    return null;
}
/** Maps every node of `a` to the node at the same position in its clone `b` (and their parts in `parts`). */
function pairNodes(a, b, map, parts) {
    a.forEach((node, i) => {
        const twin = b[i];
        map.set(node, twin);
        if (node.type === 'detailList' && twin.type === 'detailList')
            node.items.forEach((it, j) => parts.set(it, twin.items[j]));
        if (node.type === 'optionList' && twin.type === 'optionList')
            node.items.forEach((it, j) => parts.set(it, twin.items[j]));
        if (node.type === 'table' && twin.type === 'table')
            node.columns.forEach((c, j) => parts.set(c, twin.columns[j]));
        const ca = childLists(node);
        const cb = childLists(twin);
        ca.forEach((c, j) => pairNodes(c.list, cb[j].list, map, parts));
    });
}
/** Drops review ghosts (nodes and rows) and `edit` marks (nodes and parts) from a node list, in place. */
function purge(list, keepMarks) {
    for (let i = list.length - 1; i >= 0; i--) {
        const node = list[i];
        if (!keepMarks && isGhost(node)) {
            list.splice(i, 1);
            continue;
        }
        if (!keepMarks) {
            delete node.edit;
            if (node.type === 'detailList') {
                node.items = node.items.filter((it) => !isGhostRow(it));
                for (const it of node.items)
                    delete it.edit;
            }
            else if (node.type === 'optionList') {
                for (const it of node.items)
                    delete it.edit;
            }
            else if (node.type === 'table') {
                for (const c of node.columns)
                    delete c.edit;
                delete node.captionEdit;
            }
        }
        for (const { list: child } of childLists(node))
            purge(child, keepMarks);
    }
}
/** The live object of a part: the row / option item / column (the owner itself for a caption). */
const partObject = (owner, part) => {
    if (part.kind === 'caption')
        return owner;
    if (part.kind === 'row')
        return owner.type === 'detailList' ? owner.items[part.index] : undefined;
    if (part.kind === 'option')
        return owner.type === 'optionList' ? owner.items[part.index] : undefined;
    return owner.type === 'table' ? owner.columns[part.index] : undefined;
};
const labelOf = (owner, kind, object) => {
    if (kind === 'caption')
        return owner.type === 'table' ? owner.caption : undefined;
    if (kind === 'option')
        return object.content;
    if (kind === 'columnHeader')
        return object.header;
    return undefined;
};
const WHOLE_TEXT_OPS = new Set(['replaceText', 'replaceRow', 'removeNode', 'removeRow']);
const CARRY_OPS = new Set([...WHOLE_TEXT_OPS, 'insertParagraph', 'insertClause', 'insertRow']);
const isAnchor = (a) => a != null &&
    typeof a === 'object' &&
    (typeof a.fromPatch === 'string' || typeof a.instanceId === 'string');
export function applyPatches(input, patches, options = {}) {
    const track = options.track === true;
    const { resolved: _r, outline: _o, ...rest } = structuredClone(input);
    const document = rest;
    // The base is clean by contract; a tracked document passed back in must
    // not smuggle its ghosts or marks into the result.
    for (const list of regionLists(document))
        purge(list, false);
    const pristine = structuredClone(document);
    const toLive = new Map();
    const toLivePart = new Map();
    const pristineLists = regionLists(pristine);
    regionLists(document).forEach((list, i) => pairNodes(pristineLists[i], list, toLive, toLivePart));
    const pristineRoots = instanceRoots(pristine);
    /** Live nodes that are effectively locked (as compiled; inserted nodes never are). */
    const locked = new Set();
    for (const node of collectLockedNodes(pristineLists)) {
        const live = toLive.get(node);
        if (live)
            locked.add(live);
    }
    const hasLocked = (nodes) => nodes.some((n) => locked.has(n) || childLists(n).some(({ list }) => hasLocked(list)));
    const planes = new Map(document.manifest.map((m) => [m.instanceId, m.plane]));
    const references = createReferenceResolver(pristine, options.references);
    const applied = [];
    const appliedById = new Map();
    const conflicts = [];
    /**
     * Output of each applied insert. `original` is the clause's content as the
     * insert created it: a `{ fromPatch, path }` indexes THAT list, so nodes
     * other entries later insert into the clause (or undo) never shift it.
     */
    const inserted = new Map();
    /** Live node → its latest text edit. */
    const edited = new Map();
    const partEdits = new Map();
    const removedRefs = new Map();
    const seenIds = new Set();
    const detachedValues = [];
    /** Re-resolves the references in patch content; dangling own-field references become literal text. */
    const liveContent = (patch, address, content, scope) => {
        const seen = new Set();
        return references.resolveContent(structuredClone(content), scope, (v) => {
            const key = v.sourceKey ?? '';
            if (seen.has(key))
                return;
            seen.add(key);
            detachedValues.push({ patchId: patch.id, address, ...(v.sourceKey != null ? { sourceKey: v.sourceKey } : {}), text: v.text, reason: 'field_removed' });
        });
    };
    const conflict = (patch, reason, address) => {
        conflicts.push({ patchId: patch.id, op: patch.op, reason, ...(address ? { address } : {}) });
    };
    const versions = instanceBlockVersions(pristine);
    /** The patch being applied — what `resolve` consults for a version carry-over. */
    let current = null;
    const carried = new Map();
    /**
     * ADR-0103: a fingerprint mismatch is not a conflict when the patch was
     * made on an older version of the block and its target's structure is
     * unchanged. Inserts beside such a node apply as normal (not reported).
     */
    const carryOver = (instanceId, structureNow) => {
        const p = current;
        if (!p || !instanceId || !structureNow || !CARRY_OPS.has(p.op) || !p.blockVersion || p.structureFingerprint !== structureNow)
            return false;
        const now = versions.get(instanceId);
        if (!now || !isNewerBlockVersion(p.blockVersion, now))
            return false;
        if (WHOLE_TEXT_OPS.has(p.op))
            carried.set(p.id, { from: p.blockVersion, to: now });
        return true;
    };
    const record = (patch, address, instanceId) => {
        const carry = carried.get(patch.id);
        const entry = { patchId: patch.id, op: patch.op, address, ...(instanceId ? { instanceId } : {}), ...(carry ? { carriedOver: carry } : {}) };
        applied.push(entry);
        appliedById.set(patch.id, entry);
    };
    /** Whether a live node is in the document and not inside a removal. */
    const liveAt = (node) => {
        const at = locate(document, node);
        if (!at || at.ghostRow || at.ancestors.some(isGhost))
            return null;
        return at;
    };
    /** A part target whose owner is live and (rows) not removed. */
    const livePart = (t) => {
        if (!liveAt(t.owner) || isGhost(t.owner))
            return false;
        if (t.partKind === 'row') {
            const item = t.object;
            return t.owner.type === 'detailList' && t.owner.items.includes(item) && !isGhostRow(item);
        }
        return true;
    };
    /** A row (live) with locked text in its value. */
    const rowLocked = (item) => hasLocked(item.description);
    const resolve = (anchor, fp, required) => {
        if (!isAnchor(anchor))
            return { ok: false, reason: 'invalid_patch' };
        const address = formatNodeAddress(anchor);
        if ('fromPatch' in anchor) {
            const hit = inserted.get(anchor.fromPatch);
            if (!hit)
                return { ok: false, reason: 'anchor_missing', address };
            if (hit.row && !anchor.path?.length) {
                return {
                    ok: true,
                    kind: 'part',
                    owner: hit.row.owner,
                    partKind: 'row',
                    object: hit.row.item,
                    ...(hit.instanceId ? { instanceId: hit.instanceId } : {}),
                    address,
                };
            }
            let node = hit.node ?? null;
            if (anchor.path?.length)
                node = walkPath(hit.original, anchor.path);
            if (!node)
                return { ok: false, reason: 'anchor_missing', address };
            return { ok: true, kind: 'node', node, ...(hit.instanceId ? { instanceId: hit.instanceId } : {}), address };
        }
        if (required && typeof fp !== 'string')
            return { ok: false, reason: 'invalid_patch', address };
        const target = resolveAnchor(pristine, anchor, pristineRoots);
        if (!target) {
            const part = resolvePartAnchor(pristine, anchor, pristineRoots);
            if (!part)
                return { ok: false, reason: 'anchor_missing', address };
            if (fp != null &&
                (typeof fp !== 'string' || !partFingerprintMatches(part.owner, part.part, fp)) &&
                !carryOver(part.instanceId, fingerprintPartStructure(part.owner, part.part))) {
                return { ok: false, reason: 'fingerprint_mismatch', address };
            }
            const owner = toLive.get(part.owner);
            const pristineObject = partObject(part.owner, part.part);
            const object = part.part.kind === 'caption' ? owner : pristineObject ? toLivePart.get(pristineObject) : undefined;
            if (!owner || !object || !pristineObject)
                return { ok: false, reason: 'anchor_missing', address };
            return {
                ok: true,
                kind: 'part',
                owner,
                partKind: part.part.kind,
                object,
                pristineOwner: part.owner,
                pristineObject,
                instanceId: part.instanceId,
                address,
            };
        }
        if (fp != null) {
            const matches = typeof fp === 'string' &&
                (target.kind === 'instance' ? instanceFingerprintMatches(target.root.nodes, fp) : fingerprintMatches(target.node, fp));
            if (!matches && (target.kind === 'instance' || !carryOver(target.instanceId, fingerprintNodeStructure(target.node)))) {
                return { ok: false, reason: 'fingerprint_mismatch', address };
            }
        }
        if (target.kind === 'instance')
            return { ok: true, kind: 'instance', instanceId: target.instanceId, address };
        const node = toLive.get(target.node);
        if (!node)
            return { ok: false, reason: 'anchor_missing', address };
        return { ok: true, kind: 'node', node, pristine: target.node, instanceId: target.instanceId, address };
    };
    /** The live, non-removed top-level nodes of an instance (inserted siblings included). */
    const liveInstanceNodes = (instanceId) => (instanceRoots(document).get(instanceId)?.nodes ?? []).filter((n) => !isGhost(n));
    const resolveInsertPoint = (anchor, fp, position) => {
        if (position !== 'before' && position !== 'after' && position !== 'append')
            return { ok: false, reason: 'invalid_patch' };
        const t = resolve(anchor, fp, false);
        if (!t.ok)
            return t;
        if (t.kind === 'part')
            return { ok: false, reason: 'invalid_target', address: t.address };
        if (t.kind === 'instance') {
            if (position === 'append')
                return { ok: false, reason: 'invalid_target', address: t.address };
            const nodes = instanceRoots(document).get(t.instanceId)?.nodes ?? [];
            const edge = position === 'before' ? nodes[0] : nodes[nodes.length - 1];
            const at = edge ? liveAt(edge) : null;
            if (!edge || !at)
                return { ok: false, reason: 'anchor_missing', address: t.address };
            return {
                ok: true,
                list: at.list,
                index: position === 'before' ? at.index : at.index + 1,
                ref: edge,
                instanceId: t.instanceId,
                inheritInstance: t.instanceId,
                address: t.address,
            };
        }
        const at = liveAt(t.node);
        if (!at)
            return { ok: false, reason: 'anchor_missing', address: t.address };
        const base = { ok: true, ref: t.node, ...(t.instanceId ? { instanceId: t.instanceId } : {}), address: t.address };
        if (position === 'append') {
            const node = t.node;
            if (node.type !== 'section' && node.type !== 'group' && node.type !== 'notice') {
                return { ok: false, reason: 'invalid_target', address: t.address };
            }
            if (isGhost(node))
                return { ok: false, reason: 'anchor_missing', address: t.address };
            if (locked.has(node))
                return { ok: false, reason: 'locked', address: t.address };
            return { ...base, list: node.content, index: node.content.length };
        }
        const parent = at.ancestors[at.ancestors.length - 1];
        if (parent && locked.has(parent))
            return { ok: false, reason: 'locked', address: t.address };
        const inherit = t.node.instanceId;
        return { ...base, list: at.list, index: position === 'before' ? at.index : at.index + 1, ...(inherit ? { inheritInstance: inherit } : {}) };
    };
    const detachAll = (patchId, address, values) => {
        const seen = new Set();
        for (const v of values) {
            const key = valueKey(v);
            if (seen.has(key))
                continue;
            seen.add(key);
            detachedValues.push({ patchId, address, ...(v.sourceKey != null ? { sourceKey: v.sourceKey } : {}), text: v.text });
        }
    };
    const supersede = (previous, patchId) => {
        if (!previous)
            return;
        const earlier = appliedById.get(previous.patchId);
        if (earlier)
            earlier.supersededBy = patchId;
    };
    const rowContent = (patch, address, owner, instanceId) => {
        const scope = { ...(owner ? { node: owner } : {}), ...(instanceId ? { instanceId } : {}) };
        return {
            inline: (content) => liveContent(patch, address, content, scope),
            paragraphs: (runs) => runs.map((content) => ({ type: 'paragraph', content: liveContent(patch, address, content, scope) })),
        };
    };
    const validRuns = (runs) => Array.isArray(runs) && runs.length <= 100 && runs.every((r) => isAuthorableInlineContent(r));
    const markRemoved = (node, patchId, at) => {
        for (const ref of sectionRefsIn([node]))
            if (!removedRefs.has(ref))
                removedRefs.set(ref, patchId);
        // An inserted node that is removed again simply never existed.
        if (node.edit?.change === 'inserted')
            at.list.splice(at.index, 1);
        else
            node.edit = { ...node.edit, change: 'removed', patchId };
    };
    for (const patch of patches) {
        if (!patch || typeof patch !== 'object')
            continue;
        if (typeof patch.id !== 'string' || patch.id === '' || seenIds.has(patch.id)) {
            if (typeof patch.id === 'string')
                conflict(patch, 'invalid_patch');
            continue;
        }
        seenIds.add(patch.id);
        current = patch;
        const payload = (patch.payload ?? {});
        switch (patch.op) {
            case 'replaceText': {
                const t = resolve(patch.anchor, patch.fingerprint, true);
                if (!t.ok) {
                    conflict(patch, t.reason, t.address);
                    break;
                }
                if (t.kind === 'part') {
                    if (t.partKind === 'row') {
                        conflict(patch, 'invalid_target', t.address);
                        break;
                    }
                    if (locked.has(t.owner)) {
                        conflict(patch, 'locked', t.address);
                        break;
                    }
                    if (!livePart(t)) {
                        conflict(patch, 'anchor_missing', t.address);
                        break;
                    }
                    if (!isAuthorableInlineContent(payload.content)) {
                        conflict(patch, 'invalid_patch', t.address);
                        break;
                    }
                    const label = rowContent(patch, t.address, t.pristineOwner, t.instanceId).inline(payload.content);
                    if (t.partKind === 'option')
                        t.object.content = label;
                    else if (t.partKind === 'columnHeader')
                        t.object.header = label;
                    else if (t.owner.type === 'table')
                        t.owner.caption = label;
                    supersede(partEdits.get(t.object), patch.id);
                    const before = t.pristineOwner && t.pristineObject ? labelOf(t.pristineOwner, t.partKind, t.pristineObject) : undefined;
                    partEdits.set(t.object, {
                        patchId: patch.id,
                        kind: t.partKind,
                        owner: t.owner,
                        ...(before ? { before, pristineValues: collectValues(before) } : {}),
                        address: t.address,
                    });
                    record(patch, t.address, t.instanceId);
                    break;
                }
                if (t.kind === 'node' && locked.has(t.node)) {
                    conflict(patch, 'locked', t.address);
                    break;
                }
                if (t.kind !== 'node' || !isTextTarget(t.node)) {
                    conflict(patch, 'invalid_target', t.address);
                    break;
                }
                if (!liveAt(t.node) || isGhost(t.node)) {
                    conflict(patch, 'anchor_missing', t.address);
                    break;
                }
                const content = payload.content;
                if (!isAuthorableInlineContent(content)) {
                    conflict(patch, 'invalid_patch', t.address);
                    break;
                }
                const node = t.node;
                const resolved = liveContent(patch, t.address, content, { ...(t.pristine ? { node: t.pristine } : {}), ...(t.instanceId ? { instanceId: t.instanceId } : {}) });
                if (node.type === 'section') {
                    const title = inlinePlainText(resolved).replace(/\s+/g, ' ').trim();
                    if (title === '' || title.length > MAX_TITLE) {
                        conflict(patch, 'invalid_patch', t.address);
                        break;
                    }
                    node.title = title;
                }
                else if (node.type === 'paragraph' || node.type === 'heading') {
                    node.content = resolved;
                }
                const previous = edited.get(node);
                if (previous) {
                    const earlier = appliedById.get(previous.patchId);
                    if (earlier)
                        earlier.supersededBy = patch.id;
                }
                edited.set(node, { patchId: patch.id, ...(t.pristine ? { pristine: t.pristine } : {}), address: t.address });
                record(patch, t.address, t.instanceId);
                break;
            }
            case 'removeNode': {
                const t = resolve(patch.anchor, patch.fingerprint, true);
                if (!t.ok) {
                    conflict(patch, t.reason, t.address);
                    break;
                }
                if (t.kind === 'part') {
                    conflict(patch, 'invalid_target', t.address);
                    break;
                }
                if (t.kind === 'instance') {
                    const nodes = liveInstanceNodes(t.instanceId);
                    if (nodes.length === 0) {
                        conflict(patch, 'anchor_missing', t.address);
                        break;
                    }
                    if (hasLocked(nodes)) {
                        conflict(patch, 'locked', t.address);
                        break;
                    }
                    for (const node of nodes) {
                        const at = liveAt(node);
                        if (at)
                            markRemoved(node, patch.id, at);
                    }
                    record(patch, t.address, t.instanceId);
                    break;
                }
                const at = liveAt(t.node);
                if (!at || isGhost(t.node)) {
                    conflict(patch, 'anchor_missing', t.address);
                    break;
                }
                if (hasLocked([t.node])) {
                    conflict(patch, 'locked', t.address);
                    break;
                }
                markRemoved(t.node, patch.id, at);
                record(patch, t.address, t.instanceId);
                break;
            }
            case 'insertParagraph':
            case 'insertClause': {
                const point = resolveInsertPoint(patch.anchor, patch.fingerprint, payload.position);
                if (!point.ok) {
                    conflict(patch, point.reason, point.address);
                    break;
                }
                let node;
                if (patch.op === 'insertParagraph') {
                    if (!isAuthorableInlineContent(payload.content)) {
                        conflict(patch, 'invalid_patch', point.address);
                        break;
                    }
                    const scope = point.instanceId ? { instanceId: point.instanceId } : {};
                    const paragraph = { type: 'paragraph', content: liveContent(patch, point.address, payload.content, scope) };
                    const variant = payload.variant;
                    if (variant === 'lead' || variant === 'fine_print')
                        paragraph.variant = variant;
                    node = paragraph;
                }
                else {
                    const title = typeof payload.title === 'string' ? payload.title.replace(/\s+/g, ' ').trim() : '';
                    const paragraphs = payload.paragraphs;
                    if (title === '' ||
                        title.length > MAX_TITLE ||
                        !Array.isArray(paragraphs) ||
                        !paragraphs.every((p) => isAuthorableInlineContent(p))) {
                        conflict(patch, 'invalid_patch', point.address);
                        break;
                    }
                    const clause = {
                        type: 'section',
                        ref: insertedSectionRef(patch.id),
                        title,
                        numbering: 'auto',
                        content: paragraphs.map((content) => ({
                            type: 'paragraph',
                            content: liveContent(patch, point.address, content, point.instanceId ? { instanceId: point.instanceId } : {}),
                        })),
                    };
                    // Read like the neighbouring clauses.
                    const sibling = payload.position === 'append'
                        ? [...point.list].reverse().find((n) => n.type === 'section' && !isGhost(n))
                        : point.ref;
                    if (sibling?.type === 'section' && sibling.variant)
                        clause.variant = sibling.variant;
                    node = clause;
                }
                if (point.inheritInstance)
                    node.instanceId = point.inheritInstance;
                node.edit = { change: 'inserted', patchId: patch.id };
                point.list.splice(point.index, 0, node);
                inserted.set(patch.id, {
                    node,
                    original: node.type === 'section' ? [...node.content] : [],
                    ...(point.instanceId ? { instanceId: point.instanceId } : {}),
                });
                record(patch, point.address, point.instanceId);
                break;
            }
            case 'replaceRow':
            case 'removeRow': {
                const t = resolve(patch.anchor, patch.fingerprint, true);
                if (!t.ok) {
                    conflict(patch, t.reason, t.address);
                    break;
                }
                if (t.kind !== 'part' || t.partKind !== 'row') {
                    conflict(patch, 'invalid_target', t.address);
                    break;
                }
                const item = t.object;
                if (locked.has(t.owner) || rowLocked(item)) {
                    conflict(patch, 'locked', t.address);
                    break;
                }
                if (!livePart(t)) {
                    conflict(patch, 'anchor_missing', t.address);
                    break;
                }
                const pristineRow = t.pristineObject;
                if (patch.op === 'removeRow') {
                    for (const ref of sectionRefsIn(item.description))
                        if (!removedRefs.has(ref))
                            removedRefs.set(ref, patch.id);
                    if (item.edit?.change === 'inserted') {
                        const owner = t.owner;
                        owner.items.splice(owner.items.indexOf(item), 1);
                    }
                    else {
                        item.edit = { ...item.edit, change: 'removed', patchId: patch.id };
                        if (pristineRow)
                            detachAll(patch.id, t.address, rowValues(pristineRow));
                    }
                    record(patch, t.address, t.instanceId);
                    break;
                }
                const term = payload.term;
                const description = payload.description;
                if ((term === undefined && description === undefined) ||
                    (term !== undefined && !isAuthorableInlineContent(term)) ||
                    (description !== undefined && !validRuns(description))) {
                    conflict(patch, 'invalid_patch', t.address);
                    break;
                }
                const build = rowContent(patch, t.address, t.pristineOwner, t.instanceId);
                if (term !== undefined)
                    item.term = build.inline(term);
                if (description !== undefined)
                    item.description = build.paragraphs(description);
                const prior = partEdits.get(item);
                supersede(prior, patch.id);
                const termChanged = term !== undefined || prior?.before !== undefined;
                const descriptionChanged = description !== undefined || prior?.beforeDescription !== undefined;
                partEdits.set(item, {
                    patchId: patch.id,
                    kind: 'row',
                    owner: t.owner,
                    ...(pristineRow
                        ? {
                            ...(termChanged ? { before: pristineRow.term } : {}),
                            ...(descriptionChanged ? { beforeDescription: pristineRow.description } : {}),
                            pristineValues: rowValues(pristineRow),
                        }
                        : {}),
                    address: t.address,
                });
                record(patch, t.address, t.instanceId);
                break;
            }
            case 'insertRow': {
                const position = payload.position;
                if (position !== 'before' && position !== 'after' && position !== 'append') {
                    conflict(patch, 'invalid_patch');
                    break;
                }
                const t = resolve(patch.anchor, patch.fingerprint, false);
                if (!t.ok) {
                    conflict(patch, t.reason, t.address);
                    break;
                }
                let owner;
                let index;
                let pristineOwner;
                if (t.kind === 'part' && t.partKind === 'row' && position !== 'append') {
                    owner = t.owner;
                    pristineOwner = t.pristineOwner;
                    if (!livePart(t) || owner.type !== 'detailList') {
                        conflict(patch, 'anchor_missing', t.address);
                        break;
                    }
                    const at = owner.items.indexOf(t.object);
                    index = position === 'before' ? at : at + 1;
                }
                else if (t.kind === 'node' && t.node.type === 'detailList' && position === 'append') {
                    owner = t.node;
                    pristineOwner = t.pristine;
                    if (!liveAt(owner) || isGhost(owner)) {
                        conflict(patch, 'anchor_missing', t.address);
                        break;
                    }
                    index = t.node.items.length;
                }
                else {
                    conflict(patch, 'invalid_target', t.address);
                    break;
                }
                if (locked.has(owner)) {
                    conflict(patch, 'locked', t.address);
                    break;
                }
                if (!isAuthorableInlineContent(payload.term) || !validRuns(payload.description)) {
                    conflict(patch, 'invalid_patch', t.address);
                    break;
                }
                const build = rowContent(patch, t.address, pristineOwner, t.instanceId);
                const item = {
                    term: build.inline(payload.term),
                    description: build.paragraphs(payload.description),
                    edit: { change: 'inserted', patchId: patch.id },
                };
                owner.items.splice(index, 0, item);
                inserted.set(patch.id, { row: { owner, item }, original: [...item.description], ...(t.instanceId ? { instanceId: t.instanceId } : {}) });
                record(patch, t.address, t.instanceId);
                break;
            }
            case 'moveSection': {
                const t = resolve(patch.anchor, patch.fingerprint, true);
                if (!t.ok) {
                    conflict(patch, t.reason, t.address);
                    break;
                }
                if (t.kind !== 'node' || t.node.type !== 'section') {
                    conflict(patch, 'invalid_target', t.address);
                    break;
                }
                const section = t.node;
                if (hasLocked([section])) {
                    conflict(patch, 'locked', t.address);
                    break;
                }
                const from = liveAt(section);
                if (!from || isGhost(section)) {
                    conflict(patch, 'anchor_missing', t.address);
                    break;
                }
                const point = resolveInsertPoint(payload.to, payload.toFingerprint, payload.position);
                if (!point.ok) {
                    conflict(patch, point.reason, point.address ?? t.address);
                    break;
                }
                if (contains([section], point.ref) || (point.list === from.list && (point.index === from.index || point.index === from.index + 1))) {
                    conflict(patch, 'invalid_target', t.address);
                    break;
                }
                // The origin keeps a ghost (same list length, so `point.index` holds).
                const ghost = structuredClone(section);
                purge(ghost.content, false);
                ghost.edit = { change: 'removed', moved: true, patchId: patch.id };
                from.list[from.index] = ghost;
                section.edit = { ...section.edit, moved: true, movedBy: patch.id };
                point.list.splice(point.index, 0, section);
                record(patch, t.address, t.instanceId);
                break;
            }
            default:
                conflict(patch, 'invalid_patch');
        }
    }
    // ---- Results ----------------------------------------------------------
    const present = new Set();
    const presentRefs = new Set();
    const collectPresent = (list) => {
        for (const n of list) {
            if (isGhost(n))
                continue;
            present.add(n);
            if (n.type === 'section')
                presentRefs.add(n.ref);
            for (const { list: child } of childLists(n))
                if (!inGhostRow(n, child))
                    collectPresent(child);
        }
    };
    for (const list of regionLists(document))
        collectPresent(list);
    for (const [node, edit] of edited) {
        if (!edit.pristine || !present.has(node))
            continue;
        if (edit.pristine.type !== 'paragraph' && edit.pristine.type !== 'heading')
            continue;
        if (node.type !== 'paragraph' && node.type !== 'heading')
            continue;
        const kept = new Set(collectValues(node.content).map(valueKey));
        for (const v of collectValues(edit.pristine.content)) {
            if (kept.has(valueKey(v)))
                continue;
            detachedValues.push({ patchId: edit.patchId, address: edit.address, ...(v.sourceKey != null ? { sourceKey: v.sourceKey } : {}), text: v.text });
        }
    }
    for (const [object, edit] of partEdits) {
        if (!edit.pristineValues || !present.has(edit.owner))
            continue;
        let now;
        if (edit.kind === 'row') {
            const item = object;
            if (isGhostRow(item) || edit.owner.type !== 'detailList' || !edit.owner.items.includes(item))
                continue;
            now = rowValues(item);
        }
        else {
            now = collectValues(labelOf(edit.owner, edit.kind, object) ?? []);
        }
        const kept = new Set(now.map(valueKey));
        detachAll(edit.patchId, edit.address, edit.pristineValues.filter((v) => !kept.has(valueKey(v))));
    }
    const removedSections = [...removedRefs]
        .filter(([ref]) => !presentRefs.has(ref))
        .map(([ref, patchId]) => ({ ref, patchId }));
    if (track) {
        // Base nodes: the address + compiled fingerprint a new entry anchors to.
        for (const entry of listAddressableNodes(pristine)) {
            if (entry.nodeType === 'instance') {
                const first = pristineRoots.get(entry.instanceId)?.nodes[0];
                const live = first ? toLive.get(first) : undefined;
                if (live)
                    live.edit = { ...live.edit, instanceFingerprint: entry.fingerprint };
                continue;
            }
            const target = resolveAnchor(pristine, entry.anchor, pristineRoots);
            const live = target?.kind === 'node' ? toLive.get(target.node) : undefined;
            if (live)
                live.edit = { ...live.edit, address: entry.address, fingerprint: entry.fingerprint };
        }
        for (const node of locked)
            if (present.has(node))
                node.edit = { ...node.edit, locked: true };
        for (const entry of listAddressableParts(pristine)) {
            const target = resolvePartAnchor(pristine, entry.anchor, pristineRoots);
            if (!target)
                continue;
            const owner = toLive.get(target.owner);
            const pristineObject = partObject(target.owner, target.part);
            if (!owner || !pristineObject)
                continue;
            const mark = { address: entry.address, fingerprint: entry.fingerprint, ...(entry.locked ? { locked: true } : {}) };
            if (entry.part.kind === 'caption') {
                if (owner.type === 'table')
                    owner.captionEdit = { ...owner.captionEdit, ...mark };
                continue;
            }
            const object = toLivePart.get(pristineObject);
            if (object)
                object.edit = { ...object.edit, ...mark };
        }
        for (const [object, edit] of partEdits) {
            const mark = { change: 'edited', patchId: edit.patchId };
            if (edit.kind === 'row') {
                const item = object;
                if (isGhostRow(item) || item.edit?.change === 'inserted')
                    continue;
                if (edit.before)
                    mark.before = edit.before;
                if (edit.beforeDescription)
                    mark.beforeDescription = edit.beforeDescription;
                item.edit = { ...item.edit, ...mark };
                continue;
            }
            if (edit.before)
                mark.before = edit.before;
            if (edit.kind === 'caption') {
                if (edit.owner.type === 'table')
                    edit.owner.captionEdit = { ...edit.owner.captionEdit, ...mark };
                continue;
            }
            const target = object;
            target.edit = { ...target.edit, ...mark };
        }
        for (const [node, edit] of edited) {
            if (!edit.pristine || isGhost(node))
                continue;
            const mark = { change: 'edited', patchId: edit.patchId };
            if (edit.pristine.type === 'section')
                mark.beforeTitle = edit.pristine.title ?? '';
            else if (edit.pristine.type === 'paragraph' || edit.pristine.type === 'heading')
                mark.before = edit.pristine.content;
            node.edit = { ...node.edit, ...mark };
        }
        // Inserted output: `@patchId[/path]` addresses, current fingerprints.
        const stamp = (list, patchId, prefix) => {
            list.forEach((n, i) => {
                if (n.edit?.change === 'inserted' || !present.has(n))
                    return;
                const path = [...prefix, i];
                n.edit = { ...n.edit, address: formatNodeAddress({ fromPatch: patchId, path }), fingerprint: fingerprint(n) };
                for (const { segs, list: child } of childLists(n))
                    stamp(child, patchId, [...path, ...segs]);
            });
        };
        for (const [patchId, { node, row, original }] of inserted) {
            if (row) {
                if (!present.has(row.owner) || row.owner.type !== 'detailList' || !row.owner.items.includes(row.item))
                    continue;
                row.item.edit = { ...row.item.edit, address: formatNodeAddress({ fromPatch: patchId }), fingerprint: fingerprintRow(row.item) };
                stamp(original, patchId, []);
                continue;
            }
            if (!node || !present.has(node))
                continue;
            node.edit = { ...node.edit, address: formatNodeAddress({ fromPatch: patchId }), fingerprint: fingerprint(node) };
            stamp(original, patchId, []);
        }
    }
    for (const list of regionLists(document))
        purge(list, track);
    const deviatingInstances = [...new Set(applied.flatMap((a) => (a.instanceId ? [a.instanceId] : [])))].filter((id) => planes.get(id) === 'legal');
    return {
        document,
        applied,
        conflicts,
        detachedValues,
        removedSections,
        deviatesFromStandard: deviatingInstances.length > 0,
        deviatingInstances,
    };
}
//# sourceMappingURL=apply.js.map